// 3rd-party modules
import * as yup from 'yup';
import moment from "moment-timezone";
import { Button, ConfigProvider, Input, message } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';

// project modules
import { loginUser } from '../../stores/userStore';
import { useAppDispatch } from '../../hooks/storeHooks';

// apis
import * as AuthApi from '../../apis/authApi';

// models
import { ApiResponse } from '../../models/response';
import { User } from '../../models/auth';

// css and images
import Loader from '../../components/shared/loader';
import logo from "../../assets/images/logo.png";

import { UserOutlined } from "@ant-design/icons";
import { apiCall } from '../../helpers/apiHelper';
import Config from '../../config';
import useTranslation from '../../hooks/TranslationContext';
import BranchPickerModal from '../../components/branch-picker/BranchPickerModal';

type FormValues = {
  phone: string;
  code?: string;
};

export default function LoginWithSMSPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showBranchPickerModal, setShowBranchPickerModal] = useState(false);
  const [previousPhoneNumber, setPreviousPhoneNumber] = useState<string>("");
  const [timer, setTimer] = useState("00:00:00");
  const [totalRemainingTime, setTotalRemainingTime] = useState<number>(0);
  const [currentResendSMSUnblockTime, setCurrentResendSMSUnblockTime] = useState<any>(null);
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<number>(1);
  const abortController = new AbortController();
  const intervalRef = useRef<null | ReturnType<typeof setInterval>>(null);

  // Validation Schema using Yup
  const schema = yup.object().shape({
    phone: yup.string().required(t("phone-is-required")),
    code: step === 2 ? yup.string().required(t("code-is-required")) : yup.string()
  });

  const { formState: { errors }, handleSubmit, setFocus, control, getValues, setValue } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      phone: "",
      code: ""
    },
  });

  const getUserHomePage = (user?: User): string => {
    return '/';
  };

  useEffect(() => {
    setFocus('phone');

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (currentResendSMSUnblockTime) {
      // Clear the interval if the modal is shown
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Start the timer and set the interval
      if (!intervalRef.current) {
        startTimer();
        intervalRef.current = setInterval(() => startTimer(), 1000);
      }
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentResendSMSUnblockTime])

  const onSubmit = async (formData: FormValues) => {
    if (step === 1) {
      await onResendSmsClick(formData);
    }
    else {
      setLoading(true);
      const response: ApiResponse = await dispatch(loginUser(formData)).unwrap();
      setLoading(false);

      if (response.success) {
        const user = User.toClass(response.data?.value);

        if (response.data?.value?.shouldPickAResolver) {
          setShowBranchPickerModal(true);
        }
        else {
          onSelectBranchModalSave(user);
        }
      } else {
        message.error(response.errors.length > 0 ? `${response.errors[0]} ${response.errors[1]}` : response.errors[0]);
      }
    }
  };

  const onResendSmsClick = async (formData: any) => {
    setLoading(true);

    if (previousPhoneNumber === formData.phone && totalRemainingTime !== 0) {
      return;
    }

    const data = {
      phone: formData.phone
    }

    const response: ApiResponse = await apiCall(AuthApi.loginBySMS(data, abortController.signal));
    setLoading(false);

    if (response.success) {
      message.success(t("sms-sent-successfully"));
      setStep(2);
      setFocus("code");

      setCurrentResendSMSUnblockTime(moment().add(Config.resendSmsTimeout, "seconds"));
    } else {
      message.error(response.errors.length > 0 ? `${response.errors[0]} ${response.errors[1]}` : response.errors[0]);
    }
  }

  const onChangePhoneClick = () => {
    setPreviousPhoneNumber(getValues("phone"))
    setStep(1);
    setFocus("phone");
  }

  const getTimeRemaining = () => {
    const now = moment(); //todays date
    const end = moment(currentResendSMSUnblockTime); // another date
    const duration = moment.duration(end.diff(now));
    const total = duration.asSeconds();
    const seconds = Math.floor(
      total % 60
    );
    const minutes = Math.floor(
      (total / 60) % 60
    );
    const hours = Math.floor(
      (total / 60 / 60) % 24
    );
    return {
      total,
      hours,
      minutes,
      seconds,
    };
  };

  const startTimer = () => {
    let { total, hours, minutes, seconds } = getTimeRemaining();
    setTotalRemainingTime(total);
    if (total >= 0) {
      // update the timer
      // check if less than 10 then we need to
      // add '0' at the beginning of the variable
      setTimer(
        (hours ? ((hours > 9 ? hours : "0" + hours) + ":") : "") +
        (minutes > 9
          ? minutes
          : "0" + minutes) +
        ":" +
        (seconds > 9 ? seconds : "0" + seconds)
      );
    }
  };

  const onSelectBranchModalSave = (user?: User) => {
    const searchParam = searchParams.get('returnUrl');
    navigate(!!searchParam && !searchParam?.includes("login") ? searchParam : getUserHomePage(user));
  }

  const onCodeInputComplete = (input: string) => {
    setValue("code", input)
    handleSubmit(onSubmit);
  }

  return (
    <>
      <div className="mb-5">
        <img
          src={logo}
          alt="Logo"
          className="w-16 h-16"
        />
      </div>

      <div className="w-full max-w-sm">
        <h1 className="text-4xl font-bold mb-2">
          {t("aban")}{" "}<span className="text-main-color">{t("management")}</span>
        </h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div>
            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  prefix={<UserOutlined />}
                  placeholder={t("phone")}
                  disabled={step !== 1}
                  size="large"
                  className="text-lg"
                  inputMode="numeric"
                />
              )}
            />
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
            )}
          </div>
          {
            step === 2 &&
            <div dir="ltr" className="w-full">
              <ConfigProvider direction="ltr">
                <Controller
                  name="code"
                  control={control}
                  render={({ field }) => (
                    <Input.OTP
                      {...field}
                      length={6}
                      size="large"
                      className="text-lg"
                      inputMode="numeric"
                      onChange={onCodeInputComplete}
                    />
                  )}
                />
              </ConfigProvider>
              {errors.code && (
                <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
              )}
            </div>
          }

          {loading && <Loader />}
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            className="w-full bg-main hover:bg-main-dark text-white text-lg py-2 font-semibold"
            loading={loading}
          >
            {t(step === 1 ? "send-sms" : "sign-in")}
          </Button>
          {
            step === 2 &&
            <>
              <Button
                type="default"
                size="large"
                onClick={onResendSmsClick}
                className={`w-full ${totalRemainingTime > 0 ? "bg-gray-400" : "bg-main hover:bg-main-dark"} text-white text-lg py-2 font-semibold`}
                disabled={totalRemainingTime > 0}
              >
                {totalRemainingTime > 0 ? timer : t("resend-sms")}
              </Button>

              <Button
                type="default"
                size="large"
                onClick={onChangePhoneClick}
                className="w-full bg-main hover:bg-main-dark text-white text-lg py-2 font-semibold"
              >
                {t("change-phone-number")}
              </Button>
            </>
          }
        </form>
      </div>
      {
        !!showBranchPickerModal &&
        <BranchPickerModal
          open={showBranchPickerModal}
          closeOnSave={true}
          onCancel={() => setShowBranchPickerModal(false)}
          onSave={onSelectBranchModalSave} />
      }
    </>
  );
}
